import { defaultDateFormat } from "@/core/date";
import { byteSizeFormatter } from "@/core/string";
import {
    Document,
    DocumentPagination,
    getDocumentDescription,
    SearchableDocument,
    SignDocumentRequest,
} from "@/shared/domain";

import { getSpApi, pagedRequestConfig } from "./apiRoot";
import { UrlManager } from "./UrlManager";

// Swagger: https://ibpcsrcdev01.portima.net/mybrokerapi/swagger/index.html

export const documentMapper = (source: MyBrokerApi.Document): Document => {
    return {
        ...source,
        status: {
            id: source.status.id,
            name: source.status.id === "Unknown" || source.status.id === "ToBeSigned" ? "" : source.status.name,
        },
        policyholderId: source.policyHolderId,
        secondaryDescription: getDocumentDescription(source.context.name, source.origin),
    };
};

export const searchableDocumentMapper = (source: Document[]): SearchableDocument[] => {
    return source?.map((document) => ({
        ...document,
        formattedDate: defaultDateFormat(document.date),
        formattedTypeSize: `${document.documentType.name} - ${byteSizeFormatter(document.fileSize)}`,
    }));
};
/**
 * Returns all contracts documents
 */

export type SearchDocumentsQueryParameters = Paths.ApiDocumentSearch.Get.QueryParameters;

export const getDocuments = async (requestParams: SearchDocumentsQueryParameters): Promise<DocumentPagination> => {
    const axiosRequestConfig = pagedRequestConfig<SearchDocumentsQueryParameters>({ params: requestParams });

    const url = requestParams.filter === "all" ? UrlManager.searchDocuments() : UrlManager.relatedDocuments();

    const { data } = await getSpApi().get<MyBrokerApi.DocumentPayload>(url, axiosRequestConfig);

    return { ...data, result: data.result.map((item) => documentMapper(item)) };
};

export type RelatedDocumentsQueryParameters =
    | Paths.ApiDocumentRelated$Id.Get.QueryParameters
    | Paths.ApiDocumentRelatedPolicyholder$PolicyholderId.Get.QueryParameters;

export const getRelatedDocuments = async (
    id: string,
    requestParams: RelatedDocumentsQueryParameters = {},
): Promise<DocumentPagination> => {
    const { filter, ...params } = requestParams;
    const isPolicyholderType = `${filter}` === "policyholders";
    const axiosRequestConfig = pagedRequestConfig<RelatedDocumentsQueryParameters>({
        params: isPolicyholderType ? params : requestParams,
    });
    // Backend temp workaround
    const urlWorkaround = isPolicyholderType
        ? UrlManager.policyholderRelatedDocument(id)
        : UrlManager.relatedDocument(id);

    const { data } = await getSpApi().get<MyBrokerApi.DocumentPayload>(urlWorkaround, axiosRequestConfig);

    return { ...data, result: data.result.map((item) => documentMapper(item)) };
};

export const signDocumentFromRequest = async (body: SignDocumentRequest): Promise<MyBrokerApi.ApiSuccessOk> => {
    return await getSpApi().post(UrlManager.signDocument(), body);
};
